import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { finalize, map, shareReplay, switchMap, tap, catchError } from 'rxjs/operators';
import { MenuItem } from '@doc/utils';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@doc/core';
import { DocumentService } from './document.service';

export interface BreadcrumbItem {
  label: string;
  link: string;
  icon?: string;
  queryParams?: any;
  /** When set, UI should translate this key (survives language switches). */
  labelKey?: string;
}

export type SidebarContextType = 'files' | 'org-assets' | 'shared' | 'favourites' | 'recycle-bin' | 'search-items';

/** Opens the side menu folder hierarchy popup (My Files vs Department / Org Assets). */
export type FolderHierarchyOpenMode = 'personal' | 'department';

type ContextType = SidebarContextType;

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly translateService = inject(TranslateService);
  private readonly languageService = inject(LanguageService);

  constructor(private documentService: DocumentService) {
    this.languageService.currentLanguage$.subscribe(() => {
      this.refreshBreadcrumbLabelsForCurrentLanguage();
    });
  }
  private breadcrumbResolveSequence = 0;
  /** Dedupes concurrent ancestor breadcrumb fetches for the same folder. */
  private breadcrumbAncestorInFlightKey: string | null = null;
  private breadcrumbAncestorInFlight$: Observable<BreadcrumbItem[]> | null = null;

  private _breadcrumb = new BehaviorSubject<BreadcrumbItem[]>([]);
  breadcrumb$ = this._breadcrumb.asObservable();

  private _collapsed = new BehaviorSubject<boolean>(false);
  collapsed$ = this._collapsed.asObservable();

  /** Desktop preference key: collapsed means icon rail (not hidden). Mobile uses the same flag for drawer closed. */
  private static readonly DESKTOP_COLLAPSE_PREF_KEY = 'amm_sidebar_collapsed';

  private _menuInteraction = new Subject<void>();
  menuInteraction$ = this._menuInteraction.asObservable();

  // private _refreshFolderHierarchy = new BehaviorSubject<boolean>(false);
  // Using Subject for event stream instead of state to prevent sticky "true" values
  // refreshFolderHierarchy$ = this._refreshFolderHierarchy.asObservable();
  private _refreshSignal = new Subject<void>();
  refreshFolderHierarchy$ = this._refreshSignal.asObservable();

  private _openFolderHierarchyRequest = new Subject<FolderHierarchyOpenMode>();
  openFolderHierarchyRequest$ = this._openFolderHierarchyRequest.asObservable();

  private _events = new BehaviorSubject<any[]>([]);
  events$ = this._events.asObservable();
  private eventsLoaded = false;

  private _tags = new BehaviorSubject<any[]>([]);
  tags$ = this._tags.asObservable();
  private tagsLoaded = false;

  private _renameFolderId = new BehaviorSubject<number | null>(null);
  renameFolderId$ = this._renameFolderId.asObservable();

  private _openChangeProfile = new Subject<void>();
  openChangeProfile$ = this._openChangeProfile.asObservable();

  private _openChangePassword = new Subject<void>();
  openChangePassword$ = this._openChangePassword.asObservable();

  triggerChangeProfile() {
    this._openChangeProfile.next();
  }

  triggerChangePassword() {
    this._openChangePassword.next();
  }

  private currentContext: ContextType = 'files';
  private searchQueryParams: any = {};

  private _menuItems = new BehaviorSubject<MenuItem[]>([]);
  menuItems$ = this._menuItems.asObservable();

  private menuItems: MenuItem[] = [];

  /**
   * Folder trees per context
   * (important for Shared/Favourites/RecycleBin)
   */
  private contextTrees: Record<ContextType, MenuItem[]> = {
    files: [],
    'org-assets': [],
    shared: [],
    favourites: [],
    'recycle-bin': [],
    'search-items': [],
  };

  private showMyFilesRoot = false;

  /**
   * Toggle sidebar. On desktop: expanded ↔ tiny icon rail.
   * On mobile: drawer open ↔ closed.
   */
  toggleSidebar() {
    const next = !this._collapsed.value;
    this._collapsed.next(next);
    this.persistDesktopCollapsePreference(next);
    this.syncMobileSidebarBodyClass();
  }

  setCollapsed(collapsed: boolean) {
    this._collapsed.next(collapsed);
    this.persistDesktopCollapsePreference(collapsed);
    this.syncMobileSidebarBodyClass();
  }

  /** Restore desktop rail/expanded preference from localStorage (no-op on mobile). */
  restoreDesktopCollapsePreference(): void {
    if (typeof window === 'undefined' || window.innerWidth <= 768) {
      return;
    }
    const raw = localStorage.getItem(SidebarService.DESKTOP_COLLAPSE_PREF_KEY);
    if (raw === 'true') {
      this._collapsed.next(true);
    } else if (raw === 'false') {
      this._collapsed.next(false);
    }
    this.syncMobileSidebarBodyClass();
  }

  private persistDesktopCollapsePreference(collapsed: boolean): void {
    if (typeof window === 'undefined' || window.innerWidth <= 768) {
      return;
    }
    try {
      localStorage.setItem(
        SidebarService.DESKTOP_COLLAPSE_PREF_KEY,
        collapsed ? 'true' : 'false'
      );
    } catch {
      // ignore quota / private mode
    }
  }

  /** Prevent background scroll and layout shift while the mobile drawer is open. */
  syncMobileSidebarBodyClass(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      document.body.classList.remove('mobile-sidebar-open');
      return;
    }
    document.body.classList.toggle('mobile-sidebar-open', !this._collapsed.value);
  }

  isCollapsed(): boolean {
    return this._collapsed.value;
  }

  /** Desktop collapsed = icon rail visible. */
  isDesktopRail(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.innerWidth > 768 &&
      this._collapsed.value
    );
  }

  notifyMenuInteraction(): void {
    this._menuInteraction.next();
  }

  setContext(context: ContextType) {
    this.currentContext = context;
  }

  /** Active file area: My Files vs Org/Department Assets, Shared, etc. */
  getCurrentContext(): SidebarContextType {
    return this.currentContext;
  }

  getOrgAssetsRootLabelKey(): string {
    return this.shouldUseDepartmentAssetsLabel()
      ? 'APPMENU.Department Assets'
      : 'APPMENU.All Department Assets';
  }

  getOrgAssetsRootLabel(): string {
    return this.translateService.instant(this.getOrgAssetsRootLabelKey());
  }

  setSearchQueryParams(params: any) {
    this.searchQueryParams = params;
  }

  setMenuItems(items: MenuItem[]) {
    this.menuItems = items;
    this._menuItems.next(items);

    // cache context folder trees for breadcrumbs / navigation helpers
    const myFiles = items.find(i => i.label === 'Department Assets');
    if (myFiles?.subItems) {
      this.contextTrees.files = myFiles.subItems;
    }
    const orgAssets = items.find(i => i.isOrgAsset === true);
    if (orgAssets?.subItems) {
      this.contextTrees['org-assets'] = orgAssets.subItems;
    }
  }

  setBreadcrumb(items: BreadcrumbItem[]) {
    this._breadcrumb.next((items || []).map((item) => this.normalizeBreadcrumbItem(item)));
  }

  /** Re-resolve translateable root labels after language change. */
  private refreshBreadcrumbLabelsForCurrentLanguage(): void {
    const current = this._breadcrumb.value;
    if (!current?.length) {
      return;
    }
    this._breadcrumb.next(current.map((item) => this.normalizeBreadcrumbItem(item)));
  }

  private normalizeBreadcrumbItem(item: BreadcrumbItem): BreadcrumbItem {
    const labelKey = item.labelKey || this.inferBreadcrumbLabelKey(item.label);
    if (!labelKey) {
      return { ...item };
    }
    return {
      ...item,
      labelKey,
      label: this.translateService.instant(labelKey),
    };
  }

  private inferBreadcrumbLabelKey(label?: string): string | undefined {
    const normalized = String(label || '').trim();
    const map: Record<string, string> = {
      'My Files': 'APPMENU.My Files',
      'Department Assets': 'APPMENU.Department Assets',
      'All Department Assets': 'APPMENU.All Department Assets',
      Reviews: 'APPMENU.Reviews',
      Favourites: 'APPMENU.Favourites',
      Favorites: 'APPMENU.Favourites',
      'Recycle bin': 'APPMENU.Recycle bin',
      'Recycle Bin': 'APPMENU.Recycle bin',
      Shared: 'APPMENU.Shared',
      'Browse By Tags': 'MEDIALIBRARY.browseByTags',
      'Browse By Event': 'MEDIALIBRARY.browseByEvent',
    };
    return map[normalized];
  }

  /** Root label for browse-by-tag / browse-by-event (breadcrumb + page title). */
  getTagEventBrowseRootLabel(filterType: 'tag' | 'event'): string {
    return filterType === 'tag' ? 'Browse By Tags' : 'Browse By Event';
  }

  buildTagEventBrowseBreadcrumb(options: {
    filterType: 'tag' | 'event';
    tagId?: number;
    tagName?: string;
    eventId?: number;
    eventName?: string;
    folderSegments?: Array<{ id: number; label: string }>;
    fileName?: string;
  }): BreadcrumbItem[] {
    const {
      filterType,
      tagId,
      tagName,
      eventId,
      eventName,
      folderSegments = [],
      fileName,
    } = options;

    const queryParams =
      filterType === 'tag' && tagId
        ? { tagId, tagName: tagName || '' }
        : filterType === 'event' && eventId
          ? { eventId, eventName: eventName || '' }
          : undefined;

    const crumbs: BreadcrumbItem[] = [];

    for (const seg of folderSegments) {
      crumbs.push({
        label: seg.label,
        link: this.getFolderLink(seg.id, 'files'),
        icon: 'folder',
        queryParams,
      });
    }

    if (fileName) {
      crumbs.push({
        label: decodeURIComponent(fileName),
        link: '',
        icon: 'file',
        queryParams,
      });
    }

    return crumbs;
  }

  applyTagEventBrowseBreadcrumb(options: {
    filterType: 'tag' | 'event';
    tagId?: number;
    tagName?: string;
    eventId?: number;
    eventName?: string;
    folderId?: number;
    fileName?: string;
  }): void {
    const folderId = Number(options.folderId ?? 0);
    const resolvedFolderId = Number.isFinite(folderId) && folderId > 0 ? folderId : 0;
    const resolveSequence = ++this.breadcrumbResolveSequence;

    if (!resolvedFolderId) {
      this.setBreadcrumb([]);
      return;
    }

    this.fetchFolderAncestorSegments(resolvedFolderId).subscribe({
      next: (segments) => {
        if (resolveSequence !== this.breadcrumbResolveSequence) {
          return;
        }
        this.setBreadcrumb(
          this.buildTagEventBrowseBreadcrumb({
            ...options,
            folderSegments: segments,
          })
        );
      },
      error: () => {
        if (resolveSequence !== this.breadcrumbResolveSequence) {
          return;
        }
        this.setBreadcrumb(this.buildTagEventBrowseBreadcrumb(options));
      },
    });
  }

  setShowMyFilesRoot(show: boolean) {
    this.showMyFilesRoot = show;
  }

  /**
   * Trigger folder hierarchy refresh
   * Call this method from any component after folder create/update/delete operations
   */
  triggerFolderHierarchyRefresh(): void {
    this._refreshSignal.next();
  }

  /** Set during login so the sidebar can reload folder trees once /main is active. */
  scheduleHierarchyRefreshAfterLogin(): void {
    sessionStorage.setItem('amm_refreshFolderHierarchyAfterLogin', '1');
  }

  consumeScheduledHierarchyRefreshAfterLogin(): boolean {
    const pending = sessionStorage.getItem('amm_refreshFolderHierarchyAfterLogin') === '1';
    if (pending) {
      sessionStorage.removeItem('amm_refreshFolderHierarchyAfterLogin');
    }
    return pending;
  }

  /** Show the folder hierarchy modal (same as sidebar root row icon). */
  requestOpenFolderHierarchy(mode: FolderHierarchyOpenMode): void {
    this._openFolderHierarchyRequest.next(mode);
  }

  triggerRename(folderId: number): void {
    this._renameFolderId.next(folderId);
  }

  clearRename(): void {
    this._renameFolderId.next(null);
  }

  refreshFolderHierarchyGlobal(userId?: number, maxDepth?: number): Observable<any> {
    const userIdToUse = userId || Number(localStorage.getItem('userId'));

    if (!userIdToUse) {
      throw new Error('User ID is required to refresh folder hierarchy');
    }

    this.triggerFolderHierarchyRefresh();

    return this.documentService.getFolderHierarchy(userIdToUse, maxDepth);
  }

  loadEvents(forceRefresh: boolean = false): Observable<any> {
    if (this.eventsLoaded && !forceRefresh) {
      return of(this._events.value);
    }

    const userId = localStorage.getItem('userId');
    const payload = {
      pageIndex: 0,
      pageSize: 0,
      userId: Number(userId),
      permissionBy: 0,
      parentId: 0,
      folderId: 0,
      searchTerm: "",
      fileStatus: 0,
      fileId: 0
    };

    return this.documentService.getEvents(payload).pipe(
      tap((res) => {
        if (res && res.data) {
          const events = Array.isArray(res.data.items)
            ? res.data.items
            : (Array.isArray(res.data) ? res.data : []);
          this._events.next(events);
          this.eventsLoaded = true;
        }
      })
    );
  }

  loadTags(forceRefresh: boolean = false): Observable<any> {
    if (this.tagsLoaded && !forceRefresh) {
      return of(this._tags.value);
    }

    const userId = localStorage.getItem('userId');
    const payload = {
      pageIndex: 0,
      pageSize: 0,
      userId: Number(userId),
      permissionBy: 0,
      parentId: 0,
      folderId: 0,
      searchTerm: "",
      fileStatus: 0,
      fileId: 0,
      includeFileCounts: true,
    };

    return this.documentService.getTags(payload).pipe(
      tap((res) => {
        if (res && res.data) {
          const tags = Array.isArray(res.data.items)
            ? res.data.items
            : (Array.isArray(res.data) ? res.data : []);
          this._tags.next(tags);
          this.tagsLoaded = true;
        }
      }),
      catchError((error) => {
        console.error('Sidebar: failed to load tags', error);
        this._tags.next([]);
        this.tagsLoaded = false;
        return of([]);
      })
    );
  }

  // /**
  //  * Force refresh events data from API
  //  * @returns Observable with API response
  //  */
  refreshEvents(): Observable<any> {
    return this.loadEvents(true);
  }

  /**
   * Force refresh tags data from API
   * @returns Observable with API response
   */
  refreshTags(): Observable<any> {
    return this.loadTags(true);
  }

  /**
   * Get current events value synchronously (without subscription)
   * @returns Current events array
   */
  getEventsValue(): any[] {
    return this._events.value;
  }

  /**
   * Get current tags value synchronously (without subscription)
   * @returns Current tags array
   */
  getTagsValue(): any[] {
    return this._tags.value;
  }

  private contextFolderMap: Record<ContextType, Map<number, MenuItem>> = {
    files: new Map(),
    'org-assets': new Map(),
    shared: new Map(),
    favourites: new Map(),
    'recycle-bin': new Map(),
    'search-items': new Map(),
  };

  /**
   * Used by Shared / Favourites / Recycle Bin
   * Call this after every API response
   */
  registerItems(context: ContextType, apiItems: any[]) {

    apiItems
      .filter(i => i.entityType === 'Folder')
      .forEach(i => {

        const existing = this.contextFolderMap[context].get(i.entityId);

        const folder: MenuItem = {
          id: i.entityId,
          label: i.entityName,
          parentId: i.parentId ?? null,
          isFolder: true,
          subItems: [],
        };

        // update or insert
        this.contextFolderMap[context].set(folder.id, {
          ...existing,
          ...folder,
        });
      });
  }

  private buildContextBreadcrumbByParent(
    context: ContextType,
    folderId: number
  ): BreadcrumbItem[] {

    const crumbs: BreadcrumbItem[] = [];
    const map = this.contextFolderMap[context];

    let currentId: number | null = folderId;
    let safety = 0;

    while (currentId && safety < 20) {
      const folder = map.get(currentId);
      if (!folder) break;

      crumbs.unshift({
        label: folder.label,
        link: this.getFolderLink(folder.id, context),
        icon: 'folder',
        queryParams: context === 'search-items' ? this.searchQueryParams : undefined
      });

      currentId =
        folder.parentId && folder.parentId > 0
          ? folder.parentId
          : null;

      safety++;
    }

    return crumbs;
  }

  buildBreadcrumbFromPath(
    folderId: number | string,
    fileName?: string
  ): BreadcrumbItem[] {

    const breadcrumbs: BreadcrumbItem[] = [];
    const folderIdNum = Number(folderId);

    if (this.currentContext !== 'files') {

      breadcrumbs.push({
        label: this.currentContextLabel(),
        labelKey: this.currentContextLabelKey(),
        link: this.getContextLink(),
        icon: 'folder',
        queryParams: this.currentContext === 'search-items' ? this.searchQueryParams : undefined
      });

      if (!isNaN(folderIdNum) && folderIdNum > 0) {
        breadcrumbs.push(
          ...this.buildContextBreadcrumbByParent(
            this.currentContext,
            folderIdNum
          )
        );
      }

    } else {
      const path = this.findFolderPath(folderIdNum);
      breadcrumbs.push(
        ...(this.showMyFilesRoot ? path : path.slice(1))
      );
    }

    if (fileName) {
      breadcrumbs.push({
        label: decodeURIComponent(fileName),
        link: '',
        icon: 'file',
      });
    }

    return breadcrumbs;
  }

  /**
   * Human-readable folder/file path for dialogs (e.g. share). Uses ancestor API in My Files when the sidebar tree may be incomplete.
   */
  resolveFolderPathLabel$(folderId: number, fileName?: string): Observable<string> {
    const fid = Number(folderId);
    const name = fileName?.trim();

    if (this.currentContext !== 'files') {
      const resolvedFolderId = Number.isFinite(fid) && fid > 0 ? fid : 0;
      return this.resolveBreadcrumbItemsFromAncestors$(resolvedFolderId, this.currentContext, name).pipe(
        map((crumbs) => crumbs.map((c) => c.label).filter(Boolean).join(' / ')),
        catchError(() =>
          of(
            this.buildContextBreadcrumbFallback(this.currentContext, resolvedFolderId, name)
              .map((c) => c.label)
              .filter(Boolean)
              .join(' / ')
          )
        )
      );
    }

    if (!fid || fid <= 0 || Number.isNaN(fid)) {
      const root = this.getRootMenuItem();
      const rootLabel = root?.label?.trim() || '';
      if (name) {
        return of(rootLabel ? `${rootLabel} / ${name}` : name);
      }
      return of(rootLabel);
    }

    return this.fetchFolderAncestorSegments(fid).pipe(
      map((segments) => {
        const crumbs = this.filesBreadcrumbFromSegments(segments, name);
        return crumbs.map((c) => c.label).filter(Boolean).join(' / ');
      }),
      catchError(() =>
        of(
          this.buildBreadcrumbFromPath(fid, name)
            .map((c) => c.label)
            .filter(Boolean)
            .join(' / '),
        ),
      ),
    );
  }

  /**
   * True when the menu-derived trail does not include the active folder link (tree incomplete or stale).
   */
  isFilesBreadcrumbIncompleteForFolder(folderId: number, crumbs: BreadcrumbItem[]): boolean {
    const fid = Number(folderId);
    if (!fid || fid <= 0 || Number.isNaN(fid)) {
      return false;
    }
    if (this.currentContext !== 'files') {
      return false;
    }
    return !crumbs.some(
      (c) =>
        c.icon === 'folder' &&
        typeof c.link === 'string' &&
        (c.link === `/main/home/files/${fid}` || c.link.endsWith(`/main/home/files/${fid}`))
    );
  }

  /**
   * Build My Files breadcrumb from parent chain via API (always full path).
   */
  applyFilesBreadcrumbFromAncestorApi(folderId: number, fileName?: string): void {
    this.applyContextBreadcrumbFromAncestorApi(folderId, fileName, 'files');
  }

  /**
   * Unified breadcrumb resolver for all library contexts using folder ancestors.
   * This avoids flattened trails when sidebar trees are partial/stale.
   */
  applyContextBreadcrumbFromAncestorApi(
    folderId: number,
    fileName?: string,
    contextOverride?: ContextType
  ): void {
    const context: ContextType = contextOverride ?? this.currentContext;
    const fid = Number(folderId);
    const resolvedFolderId = Number.isFinite(fid) && fid > 0 ? fid : 0;
    const resolveSequence = ++this.breadcrumbResolveSequence;
    const cacheKey = `${context}:${resolvedFolderId}:${fileName || ''}`;

    const applyResult = (crumbs: BreadcrumbItem[]) => {
      if (resolveSequence !== this.breadcrumbResolveSequence) {
        return;
      }
      this.setBreadcrumb(crumbs);
    };

    // Reuse in-flight request when menuItems$ + other callers race after folder create.
    if (
      this.breadcrumbAncestorInFlightKey === cacheKey &&
      this.breadcrumbAncestorInFlight$
    ) {
      this.breadcrumbAncestorInFlight$.subscribe({
        next: applyResult,
        error: () =>
          applyResult(
            this.buildContextBreadcrumbFallback(context, resolvedFolderId, fileName)
          ),
      });
      return;
    }

    this.breadcrumbAncestorInFlightKey = cacheKey;
    this.breadcrumbAncestorInFlight$ = this.resolveBreadcrumbItemsFromAncestors$(
      resolvedFolderId,
      context,
      fileName
    ).pipe(
      shareReplay(1),
      finalize(() => {
        if (this.breadcrumbAncestorInFlightKey === cacheKey) {
          this.breadcrumbAncestorInFlightKey = null;
          this.breadcrumbAncestorInFlight$ = null;
        }
      })
    );

    this.breadcrumbAncestorInFlight$.subscribe({
      next: applyResult,
      error: () =>
        applyResult(
          this.buildContextBreadcrumbFallback(context, resolvedFolderId, fileName)
        ),
    });
  }

  private resolveBreadcrumbItemsFromAncestors$(
    folderId: number,
    context: ContextType,
    fileName?: string
  ): Observable<BreadcrumbItem[]> {
    if (!folderId || folderId <= 0 || Number.isNaN(folderId)) {
      return of(this.buildAncestorBreadcrumbForContext(context, [], fileName));
    }

    return this.fetchFolderAncestorSegments(folderId).pipe(
      map((segments) => this.buildAncestorBreadcrumbForContext(context, segments, fileName))
    );
  }

  private buildContextBreadcrumbFallback(
    context: ContextType,
    folderId: number,
    fileName?: string
  ): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [];

    if (context !== 'files') {
      breadcrumbs.push({
        label: this.currentContextLabel(context),
        labelKey: this.currentContextLabelKey(context),
        link: this.getContextLink(context),
        icon: 'folder',
        queryParams: context === 'search-items' ? this.searchQueryParams : undefined
      });
      if (folderId > 0) {
        breadcrumbs.push(...this.buildContextBreadcrumbByParent(context, folderId));
      }
    } else {
      const path = this.findFolderPath(folderId);
      breadcrumbs.push(...(this.showMyFilesRoot ? path : path.slice(1)));
    }

    if (fileName) {
      breadcrumbs.push({
        label: decodeURIComponent(fileName),
        link: '',
        icon: 'file'
      });
    }

    return breadcrumbs;
  }

  private fetchFolderAncestorSegments(
    folderId: number
  ): Observable<Array<{ id: number; label: string }>> {
    return this.documentService.getFolderById(folderId).pipe(
      switchMap((res: any) => {
        const d = res?.data;
        if (!d) {
          return of([]);
        }
        const id = Number(d.folderId ?? d.FolderId ?? folderId);
        const parentRaw = d.parentId ?? d.ParentId;
        const folderNameEn = String(d.folderNameEn ?? d.FolderNameEn ?? '').trim();
        const folderNameAr = String(d.folderNameAr ?? d.FolderNameAr ?? '').trim();
        const isValidName = (name: string) =>
          !!name && name.toLowerCase() !== 'null' && name.toLowerCase() !== 'undefined';
        const lang = this.languageService.getLanguagefromStore() || 'en';
        const label =
          (lang === 'ar'
            ? (isValidName(folderNameAr) ? folderNameAr : folderNameEn)
            : (isValidName(folderNameEn) ? folderNameEn : folderNameAr)) || 'Folder';

        let parentId: number | null = null;
        if (parentRaw !== null && parentRaw !== undefined && parentRaw !== '') {
          const p = Number(parentRaw);
          if (Number.isFinite(p) && p > 0) {
            parentId = p;
          }
        }

        if (parentId === null) {
          return of([{ id, label }]);
        }

        return this.fetchFolderAncestorSegments(parentId).pipe(
          map((ancestors) => [...ancestors, { id, label }])
        );
      })
    );
  }

  private filesBreadcrumbFromSegments(
    segments: Array<{ id: number; label: string }>,
    fileName?: string
  ): BreadcrumbItem[] {
    const rootItem = this.getRootMenuItem('files');
    const path: BreadcrumbItem[] = [];
    if (rootItem) {
      path.push({
        label: rootItem.label,
        link: rootItem.link || '',
        icon: rootItem.icon,
      });
    }
    for (const seg of segments) {
      path.push({
        label: seg.label,
        link: this.getFolderLink(seg.id, 'files'),
        icon: 'folder',
      });
    }
    if (fileName) {
      path.push({
        label: decodeURIComponent(fileName),
        link: '',
        icon: 'file',
      });
    }
    return this.showMyFilesRoot ? path : path.slice(1);
  }

  private buildAncestorBreadcrumbForContext(
    context: ContextType,
    segments: Array<{ id: number; label: string }>,
    fileName?: string
  ): BreadcrumbItem[] {
    if (context === 'files') {
      return this.filesBreadcrumbFromSegments(segments, fileName);
    }

    const path: BreadcrumbItem[] = [
      {
        label: this.currentContextLabel(context),
        labelKey: this.currentContextLabelKey(context),
        link: this.getContextLink(context),
        icon: 'folder',
        queryParams: context === 'search-items' ? this.searchQueryParams : undefined
      }
    ];

    const effectiveSegments =
      context === 'org-assets'
        ? this.trimDuplicateOrgAssetsRootSegment(segments, this.currentContextLabel(context))
        : segments;

    for (const seg of effectiveSegments) {
      path.push({
        label: seg.label,
        link: this.getFolderLink(seg.id, context),
        icon: 'folder'
      });
    }

    if (fileName) {
      path.push({
        label: decodeURIComponent(fileName),
        link: '',
        icon: 'file'
      });
    }

    return path;
  }

  private trimDuplicateOrgAssetsRootSegment(
    segments: Array<{ id: number; label: string }>,
    rootLabel: string
  ): Array<{ id: number; label: string }> {
    if (!segments?.length) {
      return segments;
    }
    const [first, ...rest] = segments;
    return this.areEquivalentOrgAssetsLabels(first?.label, rootLabel) ? rest : segments;
  }

  private areEquivalentOrgAssetsLabels(a: string, b: string): boolean {
    const normalize = (value: string): string =>
      String(value || '')
        .toLowerCase()
        .replace(/[/_-]+/g, ' ')
        .replace(/\b(department|library|asset|assets|org|organization)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const left = normalize(a);
    const right = normalize(b);
    if (!left || !right) {
      return false;
    }
    return left === right || left.includes(right) || right.includes(left);
  }

  private findFolderPath(folderId: number | string): BreadcrumbItem[] {
    const path: BreadcrumbItem[] = [];
    const targetId = Number(folderId);

    const rootItem = this.getRootMenuItem('files');
    if (!rootItem) return path;

    path.push({
      label: rootItem.label,
      link: rootItem.link || '',
      icon: rootItem.icon,
    });

    const found = this.searchFolderInHierarchy(
      rootItem.subItems || [],
      targetId,
      [],
      'files'
    );

    if (found) {
      path.push(...found);
    }

    return path;
  }

  private searchFolderInHierarchy(
    items: MenuItem[],
    targetId: number,
    currentPath: BreadcrumbItem[],
    context: ContextType = this.currentContext
  ): BreadcrumbItem[] | null {

    for (const item of items) {
      // Check if this is the target folder
      if (Number(item.id) === targetId) {
        // Return the path INCLUDING this folder (regardless of parentId)
        return [
          ...currentPath,
          {
            label: item.label,
            link: this.getFolderLink(item.id, context),
            icon: item.icon || 'folder',
          }];
      }

      // If this folder has subfolders, search recursively
      if (item.isFolder && item.subItems?.length) {
        const found = this.searchFolderInHierarchy(
          item.subItems,
          targetId,
          [
            ...currentPath,
            {
              label: item.label,
              link: this.getFolderLink(item.id, context),
              icon: item.icon || 'folder',
            }],
          context
        );
        if (found) return found;
      }
    }

    return null;
  }

  private getFolderLink(folderId: number, context: ContextType = this.currentContext): string {
    switch (context) {
      case 'org-assets':
        return `/main/home/dept-assets/${folderId}`;
      case 'shared':
        return `/main/home/reviews/${folderId}`;
      case 'favourites':
        return `/main/home/favourites/${folderId}`;
      case 'recycle-bin':
        return `/main/home/recycle-bin/${folderId}`;
      case 'search-items':
        return `/main/home/search-items/${folderId}`;
      default:
        return `/main/home/files/${folderId}`;
    }
  }

  private getContextLink(context: ContextType = this.currentContext) {
    switch (context) {
      case 'org-assets':
        return '/main/home/dept-assets';
      case 'shared':
        return '/main/home/reviews';
      case 'favourites':
        return '/main/home/favourites';
      case 'recycle-bin':
        return '/main/home/recycle-bin';
      case 'search-items':
        return '/main/home/search-items';
      default:
        return '/main/home/files';
    }
  }

  private getRootMenuItem(context: ContextType = this.currentContext): MenuItem | undefined {
    if (context === 'files') {
      return this.menuItems.find(i => i.label === 'My Files');
    }
    if (context === 'org-assets') {
      return this.menuItems.find(i => i.isOrgAsset);
    }

    return {
      id: 0,
      label: this.currentContextLabel(context),
      link: this.getContextLink(context),
      isFolder: true,
      subItems: this.contextTrees[context],
    };
  }

  private currentContextLabel(context: ContextType = this.currentContext): string {
    const key = this.currentContextLabelKey(context);
    if (key) {
      return this.translateService.instant(key);
    }
    if (context === 'search-items') {
      return 'Search Items';
    }
    return 'Department Assets';
  }

  private currentContextLabelKey(context: ContextType = this.currentContext): string | undefined {
    switch (context) {
      case 'org-assets':
        return this.shouldUseDepartmentAssetsLabel()
          ? 'APPMENU.Department Assets'
          : 'APPMENU.All Department Assets';
      case 'shared':
        return 'APPMENU.Reviews';
      case 'favourites':
        return 'APPMENU.Favourites';
      case 'recycle-bin':
        return 'APPMENU.Recycle bin';
      case 'search-items':
        return 'APPHEADER.search.resultsTitle';
      default:
        return 'APPMENU.Department Assets';
    }
  }

  private shouldUseDepartmentAssetsLabel(): boolean {
    return !this.hasFullAccess();
  }

  private hasFullAccess(): boolean {
    return localStorage.getItem('isFullAccess') === 'true';
  }

  private mergeFolders(existing: MenuItem[], incoming: MenuItem[]) {

    // First pass: add all folders (no nesting yet)
    for (const folder of incoming) {
      if (!this.findFolder(existing, folder.id)) {
        existing.push({ ...folder, subItems: [] });
      }
    }

    // Second pass: nest correctly
    for (const folder of incoming) {
      if (folder.parentId) {
        const parent = this.findFolder(existing, folder.parentId);
        const child = this.findFolder(existing, folder.id);

        if (parent && child && !parent.subItems?.includes(child)) {
          parent.subItems = parent.subItems || [];
          parent.subItems.push(child);

          // remove child from root
          const idx = existing.indexOf(child);
          if (idx !== -1) existing.splice(idx, 1);
        }
      }
    }
  }

  private findFolder(items: MenuItem[], id: number): MenuItem | null {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.subItems?.length) {
        const found = this.findFolder(item.subItems, id);
        if (found) return found;
      }
    }
    return null;
  }
}